import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import { usePopupController } from '@atlassian/jira-servicedesk-common/src/use-popup-controller';
import { HasPermission } from '../../../common/ui/has-permission/index.tsx';
import { ReusableSlaCalendarTooltipStyles } from '../../../common/ui/styled';
// eslint-disable-next-line import/order
import type { Calendar } from '../../../common/utils/calendars/types.tsx';

import { getCalendarIconTooltipContentUtil } from '../../reusable-calendars/summary-with-actions';
import { DeleteCalendarModal } from './delete-modal';
import messages from './messages';

type Props = {
	isOpen: boolean;
	canCloneCalendar: boolean;
} & {
	calendar: Calendar;
	userTimeZone: string;
	onToggleDetails: () => void;
	onEditRequested: () => void;
	onDeleteCalendarSuccessful: (calendarRowIndex: number) => void;
	onCloneCalendar: (calendar: Calendar) => void;
};

type DeleteDropdownProps = {
	calendarId: number;
	activeCalendarContent: string;
	deleteCalendarContent: string;
};

const dataTestIdPrefix = 'servicedesk-sla-settings.ui.calendars.summary-with-actions';
const EMPTY_TOOLTIP = '';

export const DeleteDropdown = ({
	calendarId,
	activeCalendarContent,
	deleteCalendarContent,
}: DeleteDropdownProps) => (
	<>
		<Tooltip component={ReusableSlaCalendarTooltipStyles} content={activeCalendarContent} truncate>
			<DropdownItem isDisabled data-test-id={`${dataTestIdPrefix}.new-delete-button-${calendarId}`}>
				{deleteCalendarContent}
			</DropdownItem>
		</Tooltip>
	</>
);

const SummaryView = ({
	isOpen,
	calendar,
	onToggleDetails,
	onDeleteCalendarSuccessful,
	onEditRequested,
	onCloneCalendar,
	canCloneCalendar,
	userTimeZone,
}: Props) => {
	const { formatMessage } = useIntl();
	const { baseUrl } = useCommonPageContext();
	const [isDeleteModalOpen, toggleDeleteModal] = usePopupController(false);
	const {
		id: calendarId,
		name,
		canDelete,
		canUpdate,
		deletable,
		lastUpdatedEpoch,
		createdEpoch,
	} = calendar;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getCalendarIconTooltipContent = (): string => {
		if (!lastUpdatedEpoch) {
			if (createdEpoch) {
				const createdEpochTooltipString: string = getCalendarIconTooltipContentUtil(
					createdEpoch,
					userTimeZone,
				);
				return formatMessage(messages.createdOn, { date: createdEpochTooltipString });
			}
			return EMPTY_TOOLTIP;
		}

		const updatedEpochTooltipString: string = getCalendarIconTooltipContentUtil(
			lastUpdatedEpoch,
			userTimeZone,
		);

		return formatMessage(messages.updatedOn, { date: updatedEpochTooltipString });
	};

	return (
		<HeadingContainer>
			<TextContainer data-test-id={`${dataTestIdPrefix}.calendar-name-${calendarId}`}>
				<Box xcss={iconContainerStyles}>
					<Tooltip
						component={ReusableSlaCalendarTooltipStyles}
						content={getCalendarIconTooltipContent()}
						truncate
					>
						{}
						<CalendarIcon
							LEGACY_size="large"
							LEGACY_primaryColor={token('color.text.subtle', colors.N500)}
							label=""
							color="currentColor"
						/>
					</Tooltip>
				</Box>
				{name}
			</TextContainer>
			<ActionsContainer>
				<DropdownMenu
					shouldFlip={false}
					placement="bottom-end"
					shouldRenderToParent
					trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
						<Button
							{...props}
							appearance="subtle"
							spacing="compact"
							data-test-id={`${dataTestIdPrefix}.menu-button-${calendarId}`}
							iconBefore={<MoreIcon label="" size="medium" />}
							ref={triggerRef}
							aria-label={formatMessage(messages.more, { name })}
						/>
					)}
				>
					<DropdownItemGroup>
						<HasPermission hasPermission={canUpdate}>
							<DropdownItem
								onClick={onEditRequested}
								data-test-id={`${dataTestIdPrefix}.edit-button-${calendarId}`}
							>
								{formatMessage(messages.editCalendar)}
							</DropdownItem>
						</HasPermission>
						<HasPermission hasPermission={canCloneCalendar}>
							<DropdownItem
								onClick={() => {
									fireUIAnalytics(createAnalyticsEvent({}), 'copySLACalendar clicked');
									onCloneCalendar(calendar);
								}}
								data-test-id={`${dataTestIdPrefix}.clone-button-${calendarId}`}
							>
								{formatMessage(messages.cloneCalendars)}
							</DropdownItem>
						</HasPermission>
						<HasPermission hasPermission={canDelete}>
							{deletable ? (
								<DropdownItem
									onClick={toggleDeleteModal}
									data-test-id={`${dataTestIdPrefix}.delete-button-${calendarId}`}
								>
									{formatMessage(messages.deleteCalendar)}
								</DropdownItem>
							) : (
								// Extracting the logic into DeleteDropdown for better readability
								<DeleteDropdown
									calendarId={calendarId}
									activeCalendarContent={formatMessage(messages.activeCalendar)}
									deleteCalendarContent={formatMessage(messages.deleteCalendar)}
								/>
							)}
						</HasPermission>
					</DropdownItemGroup>
				</DropdownMenu>

				{isOpen ? (
					<Button
						appearance="subtle-link"
						onClick={onToggleDetails}
						testId={`${dataTestIdPrefix}.toggle-button-${calendarId}`}
					>
						<ChevronContainer>
							<HipchatChevronDownIcon label={formatMessage(messages.hideDetails, { name })} />
						</ChevronContainer>
					</Button>
				) : (
					<Button
						appearance="subtle-link"
						onClick={onToggleDetails}
						testId={`${dataTestIdPrefix}.toggle-button-${calendarId}`}
					>
						<ChevronContainer>
							<ChevronRightLargeIcon label={formatMessage(messages.showDetails, { name })} />
						</ChevronContainer>
					</Button>
				)}
			</ActionsContainer>
			<DeleteCalendarModal
				key={calendarId}
				isOpen={isDeleteModalOpen}
				onClose={toggleDeleteModal}
				calendarId={calendarId}
				calendarName={name}
				// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
				baseUrl={baseUrl}
				isOtherProjectCalendar={false}
				onDeleteCalendarSuccessful={onDeleteCalendarSuccessful}
			/>
		</HeadingContainer>
	);
};

export const Summary = SummaryView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 7}px`,
	paddingLeft: token('space.200', '16px'),
});

const iconContainerStyles = xcss({
	paddingRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChevronContainer = styled.div({
	paddingTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	fontStretch: 'normal',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N700),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	alignItems: 'center',
});
